<template>
  <v-card
    data-component-id="RecruiterStatusRecommended"
  >
    <v-card-title
      class="text-h5"
    >
      Associer candidat à une offre
    </v-card-title>
    <v-progress-linear
      v-if="isLoading"
      indeterminate
      color="primary"
    />
    <v-card-text>
      <v-form
        v-model="isValidForm"
      >
        <template
          v-for="(field, fieldIndex) in fields"
        >
          <div
            :key="fieldIndex"
          >
            <PositionInput
              v-if="field.name === 'entityUuid'"
              v-model="data[field.name]"
              :field="{
                ...field,
                disabled: isLoading
              }"
              :classes="getInputClasses(field)"
              @input="data.offerUuid = null;"
            />
            <PositionInput
              v-else-if="field.name === 'offerUuid'"
              v-model="data[field.name]"
              :view-only="!data.entityUuid"
              :field="{
                ...field,
                additionalFilters: [
                  {
                    match: {
                      'entity.uuid.raw': data.entityUuid
                    }
                  },
                  {
                    match: {
                      status: 'published'
                    }
                  },
                  ...(data.offer?.uuid ? [{
                    bool: {
                      must_not: [
                        { term: { 'uuid.raw': data.offer.uuid } }
                      ]
                    }
                  }] : [])
                ],
                disabled: isLoading
              }"
              :classes="getInputClasses(field)"
            />
            <GenericField
              v-else
              v-model="data.message"
              :field="{
                ...field,
                disabled: isLoading
              }"
            />
          </div>
        </template>
      </v-form>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn
        text
        @click="handleDisagree"
      >
        Annuler
      </v-btn>
      <v-btn
        :disabled="!isValidForm || isLoading"
        color="primary"
        text
        @click="handleAccept"
      >
        Accepter
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState } from 'vuex';

import moment from 'moment';

import { requests } from '@/mixins/requests';
import ApplicationService from '@/services/ApplicationService';

import PositionInput from '@/components/forms/PositionInput.vue';
import GenericField from '@/components/dynamic/form/templates/GenericField.vue';

export default {
  name: 'RecruiterStatusRecommended',
  components: { GenericField, PositionInput },
  props: {
    fields: {
      type: Array,
      default: () => []
    },
    item: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      data: {},
      isValidForm: false,
      isLoading: false
    };
  },
  computed: { ...mapState(['user']) },
  watch: {
    item: {
      handler () {
        this.initializeData();
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    getNestedValue (obj, path) {
      return path
        .split('.')
        .reduce((acc, part) => (acc && acc[part]), obj);
    },
    initializeData () {
      const item = JSON.parse(JSON.stringify(this.item));

      this.fields.forEach(field => {
        this.$set(
          this.data,
          field.name,
          field.computed ? this.getNestedValue(item, field.computed) : item[field.name]
        );
      });
    },
    generateUniqueId () {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 15);
      const uniqueId = `${timestamp}_${random}`;
      return uniqueId;
    },
    async handleAccept () {
      this.$emit('handle-loading', true);
      this.isLoading = true;

      const {
        entityUuid,
        offerUuid,
        message
      } = this.data;

      let transformedNotes = null;

      if (['candidatures', 'offer-candidatures', 'candidature-spontanee-list'].includes(this.$router.currentRoute.name)) {
        const response = await this.fetchApplication();

        this.data.notes = response.notes || [];
      }

      if (this.item.notes) {
        this.data.notes = this.item.notes;
      }

      if (!this.data.notes) {
        this.data.notes = [];
      }

      transformedNotes = this.data.notes.map(note => ({
        ...note,
        collaborator: note.collaborator?.uuid || note.collaborator
      }));

      if (message) {
        transformedNotes.push({
          id: `note_${this.generateUniqueId()}`,
          collaborator: this.user.uuid,
          text: message,
          createTime: `${moment().toISOString()}`
        });
      }

      await this.saveApplication({
        workspace: 'collaborator',
        status: 'draft',
        uuid: this.item.uuid,

        entityUuid,
        offerUuid,
        notes: transformedNotes,

        recruiter: this.user.uuid,
        recruiterStatus: 'RECOMMENDED',
        prospectStatus: 'IN_PROCESS',
        stared: true
      }, true);

      await ApplicationService.notifyRecommendation({
        applicationUuid: this.item.uuid,
        offerUuid,
        senderUuid: this.user.uuid
      });

      this.isLoading = false;
      this.$emit('handle-loading', false);

      const routeMapping = {
        candidature: { name: 'offer-candidatures', params: { offerUuid: this.$route.params.offerUuid } },
        'candidature-spontanee': { name: 'candidature-spontanee-list' }
      };

      const targetRoute = routeMapping[this.$router.currentRoute.name];
      if (targetRoute) {
        this.$router.push(targetRoute);
      }

      this.$emit('close-dialog');
    },
    async saveApplication (input, isEditMode) {
      const response = await requests.saveApplication({
        input,
        isEditMode: isEditMode
      });

      if (!response.success) {
        this.$store.commit('setSnackbar', { text: 'Erreur lors de l\'enregistrement.', color: 'error' });
        this.handleDisagree();
      } else {
        this.$store.commit('setSnackbar', { text: 'Enregistrer avec succès.', color: 'success' });
      }
    },
    async fetchApplication () {
      this.$emit('handle-loading', true);

      const response = await requests.getApplication(this.item.uuid);

      if (response?.success) {
        return response.data;
      }

      this.$emit('handle-loading', false);

      return null;
    },
    handleDisagree () {
      this.$emit('disagree');
      this.$emit('handle-loading', false);
    },
    getInputClasses (field) {
      const classes = [field.mandatory ? 'required' : ''];

      if (this.isViewMode) classes.push('input-color-black');

      return classes.join(' ');
    }
  }
};
</script>
